// Liquid DnB instruments (docs/AUDIO_DESIGN.md §12), used only by dnb.js. Chords: a warm pad, per note 2 detuned soft-saw
// PeriodicWave oscillators under an octave sine "chime" that pings on every strike (cap with stealing, one voice per pitch,
// so held chords and common tones tie over the bar line), through a low-pass that swells on each new chord and a stereo
// chorus. Sub (a sine + its octave through a soft tanh), break kit (round kick, dusty snare with ghosts, 16th hats, a 32nd
// ratchet, ride, shaker, rim; one noise source and a tanh glue before the kit low-pass) and lead (an airy breath voice or a
// glassy FM pluck in a ping-pong echo) are persistent and gated with setTargetAtTime.
import { clamp } from '../../engine/core.js';
import { mtof, hold, noise } from '../dsp.js';
import { LEVELS, gain, filter, osc, panner, persistent as persist } from '../instruments.js';

// Level knobs (the harness zeroes them to measure one part alone).
export const DNB_LEVELS = { pad: 1, bass: 1, kit: 1, lead: 1 };

const lerp = (a, b, x) => a + (b - a) * x;
const stopAt = (s, t) => { try { s.stop(t); } catch { /* not started or already stopped */ } };
/** soft saturation with unity small-signal gain, y = tanh(kx) / k (odd, so no DC), 1025 points */
const softCurve = k => { const c = new Float32Array(1025); for (let i = 0; i < 1025; i++) c[i] = Math.tanh(k * (i / 512 - 1)) / k; return c; };
const shaper = (ac, k, os = 'none') => { const w = ac.createWaveShaper(); w.curve = softCurve(k); w.oversample = os; return w; };

/* ---------------- pad: 2 soft saws + an octave chime per note → swelling LP → chorus ---------------- */
// partials 1..24 of a soft saw: 1/n rolled off by 1 / (1 + (n/7)²), the 2nd a touch up: warm and round, never buzzy
const PAD_WAVE = Array.from({ length: 25 }, (_, n) => (n ? (n === 2 ? 1.15 : 1) / n / (1 + (n / 7) ** 2) : 0));
// the air lead: partials 1 · .12 · .05 (a soft flute-like sine); the rim: 500 Hz + its 3rd (.5); the ride's ring: two squares
// at 540 and 810 Hz (the 2nd and 3rd harmonics of 270, odd harmonics 1/k, .18 each, not normalised)
const AIR_WAVE = [0, 1, 0.12, 0.05];
const RIM_WAVE = [0, 1, 0, 0.5];
const RING_WAVE = Array.from({ length: 64 }, (_, n) => {
  let a = 0;
  for (const f of [2, 3]) if (n % f === 0 && (n / f) % 2 === 1) a += (0.18 * 4) / (Math.PI * (n / f));
  return a;
});
const waves = new WeakMap();
/** a cached PeriodicWave (sine partials `amps`) */
function periodic(ac, id, amps, norm = true) {
  let m = waves.get(ac);
  if (!m) waves.set(ac, (m = new Map()));
  if (!m.has(id)) m.set(id, ac.createPeriodicWave(new Float32Array(amps.length), Float32Array.from(amps), { disableNormalization: !norm }));
  return m.get(id);
}
const padWave = ac => periodic(ac, 'pad', PAD_WAVE);
const wosc = (ac, wave, f) => { const o = ac.createOscillator(); o.setPeriodicWave(wave); o.frequency.value = f; return o; };

/**
 * plan.ep.lp = the bus LP; plan.dnb.pad: { atk, rel (τ s), det (± ¢), bell, bellDecay (τ s), swell (× lp), swellTau, width,
 * chorus: { rate, depth } }. Per note (5 nodes): soft saws at ±det¢ (×0.7–1.3 at random) → 0.135·v^1.2, attack τ atk,
 * settling to .85 (τ 1.2 s); the length sets the articulation: a stab (≤ .25 s) starts in 3 ms, falls to .5 (τ 140 ms) and
 * lets go in 0.1 s. The chime (a sine at 2f, bell·A·(.5 + .5v)) pings on every strike: a fresh note, a re-strike of a
 * sounding one (×.8) and a tie (×.45), decaying with τ bellDecay. One voice per pitch: a held note struck again (or struck
 * within strum + 0.1 s of its end) carries on, drifting to its new level with no re-attack; a released one (or a stab) swells
 * back from where its release got to. Release τ rel (×.75 in lite, cap 10; the final chord: the engine's 0.9 s), sources
 * stop 5τ after the note-off (−43 dB); stealing takes the voice released first (τ 0.1 s, the chime τ 50 ms); every strike
 * logs its own [start, end]. Bus: 3 panners (the low note centred, the others alternating ±width) → LP (Q .7), swelling to
 * lp·swell (τ swellTau) on each new chord and back (τ 1.5 s) → dry .8, reverb .38, and a chorus (delays of 7 / 11 ms swept
 * in antiphase by a sine at rate Hz, wet .45 each, panned ∓.8, into the dry gain).
 */
export function createLiquidPad(ac, out, send, plan, { registry, tag, cap = 16, r = Math.random, t0 = ac.currentTime, log = null }) {
  const P = plan.dnb.pad, base = plan.ep.lp, wave = padWave(ac), nodes = [], src = [];
  const N = x => (nodes.push(x), x);
  const lp = N(filter(ac, 'lowpass', base, 0.7)), dryG = N(gain(ac, 0.8)), sendG = N(gain(ac, 0.38));
  const buses = [-P.width, 0, P.width].map(p => { const s = N(panner(ac, p)); s.connect(lp); return s; });
  lp.connect(dryG); dryG.connect(out); lp.connect(sendG); sendG.connect(send);
  const dL = N(ac.createDelay(0.05)), dR = N(ac.createDelay(0.05)), wL = N(gain(ac, 0.45)), wR = N(gain(ac, 0.45));
  const pL = N(panner(ac, -0.8)), pR = N(panner(ac, 0.8)), lfo = N(osc(ac, 'sine', P.chorus.rate));
  const gL = N(gain(ac, P.chorus.depth)), gR = N(gain(ac, -P.chorus.depth));
  dL.delayTime.value = 0.007; dR.delayTime.value = 0.011;
  lp.connect(dL); dL.connect(wL); wL.connect(pL); pL.connect(dryG);
  lp.connect(dR); dR.connect(wR); wR.connect(pR); pR.connect(dryG);
  lfo.connect(gL); gL.connect(dL.delayTime); lfo.connect(gR); gR.connect(dR.delayTime); src.push(lfo);
  const bus = persist(registry, tag, src, nodes, t0);
  const lite = cap <= 10;
  let voices = [], maxActive = 0, steals = 0, notes = 0;            // { v, midi, pad, bell, off, long, end (sounding end), rec }

  const steal = t => {
    let k = 0;
    for (let i = 1; i < voices.length; i++) if (voices[i].off < voices[k].off) k = i;
    const old = voices.splice(k, 1)[0];
    registry.stop(old.v, t, 0.1);                                    // a 0.3 s fade, not a cut (the pad: its `out`)
    hold(old.bell.gain, t); old.bell.gain.setTargetAtTime(0, t, 0.05);   // and the chime
    old.end = t; if (old.rec) old.rec[1] = t;
    steals++;
  };
  const ping = (g, t, level) => { hold(g.gain, t); g.gain.setTargetAtTime(level, t, 0.002); g.gain.setTargetAtTime(0, t + 0.01, P.bellDecay); };
  /** → true when the note (re)attacks, false when it ties */
  function note(t, midi, vel, dur, win, relTau) {
    const s = clamp((0.6 - dur) / 0.35, 0, 1), off = t + Math.max(0.06, dur), rel = lerp(relTau, 0.1, s);
    const A = 0.135 * Math.pow(vel, 1.2) * lerp(1, 1.3, s) * DNB_LEVELS.pad, B = A * P.bell * (0.5 + 0.5 * vel);
    const atk = lerp(P.atk, 0.003, s), settle = [t + lerp(2.5 * P.atk, 0.02, s), lerp(1.2, 0.14, s)];
    voices = voices.filter(x => x.end > t);
    const y = voices.find(z => z.midi === midi);
    if (y) {
      // still held: a re-strike (a cell's chime); a held note struck again where it ends: a tie; else it swells back
      const live = y.off > ac.currentTime + 0.02, held = live && t < y.off, tie = live && !held && y.long && s < 0.5 && t < y.off + win;
      const o2 = held || tie ? Math.max(off, y.off) : off, end = Math.max(o2 + 5 * rel, t + 7 * P.bellDecay), g = y.pad.gain;
      hold(g, t);
      if (tie || (held && s < 0.5)) g.setTargetAtTime(0.85 * A, t, 0.3);   // no re-attack: drift to the new level
      else { g.setTargetAtTime(A, t, atk); g.setTargetAtTime(A * lerp(0.85, 0.5, s), ...settle); }   // from where it got to
      g.setTargetAtTime(0, o2, rel);
      ping(y.bell, t, B * (held ? 0.8 : tie ? 0.45 : 1));
      for (const o of y.v.src) stopAt(o, end);
      y.v.end = end; y.off = o2; y.long = s < 0.5; y.end = Math.max(o2 + 4 * rel, t + 5 * P.bellDecay);
      // every strike logs its own [start, end] (the voice's last one ends here), so the onsets follow the events whatever
      // the voice history (a radio join or a frozen tab drops chords, which leaves other tails) and intervals never overlap
      if (log) { if (y.rec) y.rec[1] = t; y.rec = [t, y.end]; log.push(y.rec); }
      return !(held || tie);
    }
    if (voices.length >= cap) steal(t);
    const f = mtof(midi), d = P.det * (0.7 + 0.6 * r()), end = Math.max(off + 5 * rel, t + 7 * P.bellDecay);
    const o1 = osc(ac, 'sine', f), o2 = osc(ac, 'sine', f), o3 = osc(ac, 'sine', 2 * f), pad = gain(ac, 0), bell = gain(ac, 0);
    o1.setPeriodicWave(wave); o2.setPeriodicWave(wave); o1.detune.value = d; o2.detune.value = -d;
    o1.connect(pad); o2.connect(pad); o3.connect(bell);
    const bus = buses[midi < 55 ? 1 : midi % 2 ? 0 : 2];
    pad.connect(bus); bell.connect(bus);
    pad.gain.setValueAtTime(0, t);
    pad.gain.setTargetAtTime(A, t, atk);
    pad.gain.setTargetAtTime(A * lerp(0.85, 0.5, s), ...settle);
    pad.gain.setTargetAtTime(0, off, rel);
    ping(bell, t, B);
    for (const o of [o1, o2, o3]) { o.start(t); o.stop(end); }
    const v = registry.add({ src: [o1, o2, o3], all: [o1, o2, o3, pad, bell], start: t, end, tag, out: pad });
    const x = { v, midi, pad, bell, off, long: s < 0.5, end: Math.max(off + 4 * rel, t + 5 * P.bellDecay), rec: null };
    if (log) { x.rec = [t, x.end]; log.push(x.rec); }
    voices.push(x);
    notes++;
    maxActive = Math.max(maxActive, voices.length);
    return true;
  }
  return {
    /** spread bottom to top over `strum` s, the top voice +8 %; rel ≥ 0.5 (the final chord) sets the release; a chord with a
     *  fresh attack held ≥ 0.5 s swells the LP */
    chord(t, midis, vel, dur, strum = 0.03, rel = 0.08) {
      const n = midis.length, relTau = rel >= 0.5 ? rel : P.rel * (lite ? 0.75 : 1), win = strum + 0.1;
      let fresh = false;
      midis.forEach((m, k) => {
        const dt = n > 1 ? (k * strum) / (n - 1) : 0;
        if (note(t + dt, m, k === n - 1 ? Math.min(1, vel * 1.08) : vel, dur - dt, win, relTau)) fresh = true;
      });
      if (fresh && dur >= 0.5) {
        hold(lp.frequency, t);
        lp.frequency.setTargetAtTime(base * P.swell, t, P.swellTau);
        lp.frequency.setTargetAtTime(base, t + 2.5 * P.swellTau, 1.5);
      }
    },
    stats: () => ({ maxActive, steals, notes, active: voices.length }),
    dispose(t) {
      const a = Math.max(ac.currentTime, t - 1.5);                 // tails and the chorus fade before the bus is freed
      hold(dryG.gain, a); dryG.gain.setTargetAtTime(0, a, 0.25);
      hold(sendG.gain, a); sendG.gain.setTargetAtTime(0, a, 0.25);
      bus.dispose(t);
    },
  };
}

/* ---------------- sub: a sine + its octave through a soft tanh → LP (mono, persistent) ---------------- */
/** p = plan.dnb.bass: { oct (the 2f sine's level), drive (tanh k), lp (Hz), glide (τ s), rel (τ s) }. Sine f + sine 2f·oct
 *  → ×0.6 → tanh(kx)/k (odd harmonics: the 3rd and 5th carry it on phones) → LP p.lp (Q .6) → amp. A slide into another
 *  pitch while it sounds glides (τ glide); a slide on the same pitch ties (no re-attack: a chord held over the bar line);
 *  any other note re-articulates: a sounding sub dips to .35 (τ 5 ms) and comes back (τ 6 ms), a silent one starts with
 *  τ 6 ms. Amplitude .42v, settling to .88 (τ .45 s); release τ rel. */
export function createSub(ac, out, p, { registry, tag, t0 = ac.currentTime }) {
  const s1 = osc(ac, 'sine', 45), s2 = osc(ac, 'sine', 90), g2 = gain(ac, p.oct), pre = gain(ac, 0.6);
  const sh = shaper(ac, p.drive), lpf = filter(ac, 'lowpass', p.lp, 0.6), amp = gain(ac, 0);
  s1.connect(pre); s2.connect(g2); g2.connect(pre); pre.connect(sh); sh.connect(lpf); lpf.connect(amp); amp.connect(out);
  const P = persist(registry, tag, [s1, s2], [s1, s2, g2, pre, sh, lpf, amp], t0);
  let lastOff = -Infinity, lastA = 0;
  return {
    note(t, midi, vel, dur, slide = false) {
      const f = mtof(midi), A = 0.42 * vel * DNB_LEVELS.bass, off = t + Math.max(0.05, dur), on = t < lastOff + 0.03;
      for (const prm of [s1.frequency, s2.frequency, amp.gain]) hold(prm, t);
      if (slide && on) {
        s1.frequency.setTargetAtTime(f, t, p.glide); s2.frequency.setTargetAtTime(2 * f, t, p.glide);
        amp.gain.setTargetAtTime(0.88 * A, t, 0.06);
      } else {
        s1.frequency.setValueAtTime(f, t); s2.frequency.setValueAtTime(2 * f, t);
        if (on) { amp.gain.setTargetAtTime(0.35 * lastA, t, 0.005); amp.gain.setTargetAtTime(A, t + 0.016, 0.006); }
        else amp.gain.setTargetAtTime(A, t, 0.006);
        amp.gain.setTargetAtTime(0.88 * A, t + 0.06, 0.45);
      }
      amp.gain.setTargetAtTime(0, off, p.rel);
      lastOff = off; lastA = A;
    },
    dispose(t) { P.dispose(t); },
  };
}

/* ---------------- break kit: kick, snare, hats, 32nd ratchet, ride, shaker, rim; one noise source ---------------- */
/** kit = plan.drums.kit: { kickF0, kickF1, kickSweep, kickDecay, snareTone, snareF, snareDecay, hatHP, hatClosed, openDecay,
 *  rideDecay, shakerDecay, grit (tanh k), lp }. Every voice → tanh glue (k grit, 2x oversampled, none in lite) → LP kit.lp,
 *  so the break is soft and dusty; the snare, rim, ride and hats also send to the reverb. The tom lane plays the ratchet:
 *  two closed hats a 32nd apart (stepDur / 2). */
export function createBreakKit(ac, out, send, kit, { registry, tag, t0 = ac.currentTime, stepDur, lite = false }) {
  const L = LEVELS.kit * DNB_LEVELS.kit, nodes = [], N = x => (nodes.push(x), x);
  const src = N(ac.createBufferSource());
  src.buffer = noise(ac, 'white'); src.loop = true;
  const glue = N(shaper(ac, kit.grit, lite ? 'none' : '2x')), bus = N(filter(ac, 'lowpass', kit.lp, 0.5));
  glue.connect(bus); bus.connect(out);
  /** an output: a panner into the glue (null: the glue itself) and a (mono) reverb send; amp(port) gates a voice into it */
  const port = (pan, rev = 0) => {
    const outs = [pan == null ? glue : N(panner(ac, pan))];
    if (pan != null) outs[0].connect(glue);
    if (rev) { const s = N(gain(ac, rev)); s.connect(send); outs.push(s); }
    return outs;
  };
  const amp = (outs = [glue]) => { const a = N(gain(ac, 0)); for (const o of outs) a.connect(o); return a; };
  // kick: sine kickF0 (110-130 Hz) → kickF1 (45-52 Hz), τ kickSweep, decay τ kickDecay; a soft beater (noise → LP 900 Hz, τ 5 ms)
  const kOsc = N(osc(ac, 'sine', kit.kickF1)), kAmp = amp();
  kOsc.connect(kAmp);
  const kLP = N(filter(ac, 'lowpass', 900, 0.7)), kThump = amp();
  src.connect(kLP); kLP.connect(kThump);
  // snare: triangle 1.12·snareTone → snareTone (τ 25 ms) + noise HP 1 kHz → BP snareF (Q .6: the crack above the pad), τ snareDecay;
  // reverb .3
  const sPort = port(null, 0.3), sOsc = N(osc(ac, 'triangle', kit.snareTone)), sTone = amp(sPort), sNoise = amp(sPort);
  const sHP = N(filter(ac, 'highpass', 1000, 0.7)), sBP = N(filter(ac, 'bandpass', kit.snareF, 0.6));
  sOsc.connect(sTone); src.connect(sHP); sHP.connect(sBP); sBP.connect(sNoise);
  // hats and the ratchet: noise → HP hatHP; closed τ hatClosed, open τ openDecay (a closed hit chokes an open one); pan +.2, reverb .06
  const hPort = port(0.2, 0.06), hHP = N(filter(ac, 'highpass', kit.hatHP, 0.7)), hAmp = amp(hPort), tAmp = amp(hPort);
  src.connect(hHP); hHP.connect(hAmp); hHP.connect(tAmp);
  // ride (the block lane): noise → BP 5.4 kHz (Q 1.2) + a ring (squares 540 / 810 Hz at .18, one wave) → HP 4.2 kHz, τ rideDecay;
  // pan −.25, reverb .12
  const dBP = N(filter(ac, 'bandpass', 5400, 1.2)), q1 = N(wosc(ac, periodic(ac, 'ring', RING_WAVE, false), 270));
  const qHP = N(filter(ac, 'highpass', 4200, 0.7)), dAmp = amp(port(-0.25, 0.12));
  src.connect(dBP); dBP.connect(dAmp); q1.connect(qHP); qHP.connect(dAmp);
  // shaker: noise → HP 5 kHz → BP 8 kHz, attack τ 4 ms, τ shakerDecay; pan −.32
  const xHP = N(filter(ac, 'highpass', 5000, 0.7)), xBP = N(filter(ac, 'bandpass', 8000, 0.8)), xAmp = amp(port(-0.32));
  src.connect(xHP); xHP.connect(xBP); xBP.connect(xAmp);
  // rim: 500 Hz + its 3rd (.5, one wave) → HP 350, τ 8 ms; pan −.12, reverb .2
  const r1 = N(wosc(ac, periodic(ac, 'rim', RIM_WAVE), 500)), rHP = N(filter(ac, 'highpass', 350, 0.7)), rAmp = amp(port(-0.12, 0.2));
  r1.connect(rHP); rHP.connect(rAmp);
  const P = persist(registry, tag, [src, kOsc, sOsc, q1, r1], nodes, t0);
  const hit = (prm, peak, t, atk, rel, tau) => { prm.setTargetAtTime(peak, t, atk); prm.setTargetAtTime(0, t + rel, tau); };
  return {
    kick(t, v) {
      kOsc.frequency.setValueAtTime(kit.kickF0, t); kOsc.frequency.setTargetAtTime(kit.kickF1, t, kit.kickSweep);
      hit(kAmp.gain, 0.56 * v * L, t, 0.0015, 0.006, kit.kickDecay);
      hit(kThump.gain, 0.4 * v * L, t, 0.0008, 0.003, 0.005);
    },
    snare(t, v, ghost = false) {
      sOsc.frequency.setValueAtTime(kit.snareTone * 1.12, t); sOsc.frequency.setTargetAtTime(kit.snareTone, t, 0.025);
      hit(sTone.gain, 0.4 * v * L * (ghost ? 0.55 : 1), t, 0.001, 0.004, ghost ? 0.025 : 0.05);
      hit(sNoise.gain, 0.85 * v * L, t, 0.001, 0.004, ghost ? 0.028 : kit.snareDecay);
    },
    hat(t, v, open = false) { hit(hAmp.gain, 0.14 * v * L, t, 0.0008, 0.002, open ? kit.openDecay : kit.hatClosed); },
    tom(t, v) {
      hit(tAmp.gain, 0.11 * v * L, t, 0.0008, 0.002, kit.hatClosed);
      hit(tAmp.gain, 0.08 * v * L, t + stepDur / 2, 0.0008, 0.002, kit.hatClosed);
    },
    block(t, v) { hit(dAmp.gain, 0.1 * v * L, t, 0.001, 0.004, kit.rideDecay); },
    shaker(t, v) { hit(xAmp.gain, 0.1 * v * L, t, 0.004, 0.008, kit.shakerDecay); },
    rim(t, v) { hit(rAmp.gain, 0.16 * v * L, t, 0.0008, 0.002, 0.008); },
    dispose(t) { P.dispose(t); },
  };
}

/* ---------------- lead: 'air' (a breathy sine voice) or 'pluck' (glassy FM), mono, ping-pong echo ---------------- */
/** lead = plan.lead ({ voice, pan }), p = plan.dnb.lead: { vib (¢), glide (τ s), echo (wet), fb, index }. Sparse by
 *  construction: a note sooner than max(0.5 s, 5.5 steps) after the last one sounded is skipped. Echo: dotted 8ths (3
 *  steps) bouncing left / right (∓.65), wet p.echo, feedback p.fb through LP 2.2 kHz; reverb .5 on the voice and echoes. */
export function createLiquidLead(ac, out, send, lead, p, { registry, tag, stepDur, t0 = ac.currentTime }) {
  const nodes = [], src = [], N = x => (nodes.push(x), x);
  const amp = N(gain(ac, 0)), pan = N(panner(ac, lead.pan || 0)), sendG = N(gain(ac, 0.5));
  const w = N(gain(ac, p.echo)), eLP = N(filter(ac, 'lowpass', 2200, 0.5)), d1 = N(ac.createDelay(1)), d2 = N(ac.createDelay(1));
  const g12 = N(gain(ac, p.fb)), g21 = N(gain(ac, p.fb)), pL = N(panner(ac, -0.65)), pR = N(panner(ac, 0.65));
  d1.delayTime.value = d2.delayTime.value = Math.min(0.9, 3 * stepDur);
  amp.connect(pan); pan.connect(out); pan.connect(sendG); sendG.connect(send);
  amp.connect(w); w.connect(eLP); eLP.connect(d1); d1.connect(pL); d1.connect(g12); g12.connect(d2); d2.connect(pR); d2.connect(g21); g21.connect(d1);
  for (const x of [pL, pR]) { x.connect(out); x.connect(sendG); }
  const L = DNB_LEVELS.lead, gap = Math.max(0.5, 5.5 * stepDur);
  let play, offset = 0;
  if (lead.voice === 'air') {
    // a soft flute-like wave (partials 1 · .12 · .05), breath noise BP 3f (Q 1.2, .05) inside the envelope; amplitude .48v,
    // attack τ 40 ms (a glide τ p.glide), to .8 over τ .5 s, held ≥ 0.45 s, release τ .3 s; vibrato 4.8 Hz ±p.vib ¢ from 0.35 s
    const o1 = N(wosc(ac, periodic(ac, 'air', AIR_WAVE), 440));
    const nz = N(ac.createBufferSource()), nBP = N(filter(ac, 'bandpass', 1800, 1.2)), nG = N(gain(ac, 0.05));
    const vib = N(osc(ac, 'sine', 4.8)), vibG = N(gain(ac, 0));
    nz.buffer = noise(ac, 'white'); nz.loop = true; offset = 1.3;
    o1.connect(amp); nz.connect(nBP); nBP.connect(nG); nG.connect(amp);
    vib.connect(vibG); vibG.connect(o1.detune);
    src.push(o1, nz, vib);
    let lastF = 0;
    play = (t, f, vel, dur, legato) => {
      const glide = legato && lastF && Math.abs(12 * Math.log2(f / lastF)) <= 7, d = Math.max(dur, 0.45), A = 0.48 * vel * L;
      lastF = f;
      for (const prm of [o1.frequency, nBP.frequency, amp.gain, vibG.gain]) hold(prm, t);
      if (glide) o1.frequency.setTargetAtTime(f, t, p.glide); else o1.frequency.setValueAtTime(f, t);
      nBP.frequency.setTargetAtTime(Math.min(7000, 3 * f), t, 0.02);
      amp.gain.setTargetAtTime(A, t, glide ? p.glide : 0.04); amp.gain.setTargetAtTime(0.8 * A, t + 0.15, 0.5);
      amp.gain.setTargetAtTime(0, t + d, 0.3);
      vibG.gain.setTargetAtTime(0, t, 0.05);
      if (d > 0.5) vibG.gain.setTargetAtTime(p.vib, t + 0.35, 0.25);
    };
  } else {
    // pluck: FM 1:1, index p.index → .12 (τ 90 ms) → LP 3.2 kHz; amplitude .42v (attack τ 2 ms), ringing out τ .6 s, damped
    // (τ .3 s) after max(dur, .6 s)
    const car = N(osc(ac, 'sine', 440)), mod = N(osc(ac, 'sine', 440)), mG = N(gain(ac, 0)), lp = N(filter(ac, 'lowpass', 3200, 0.5));
    mod.connect(mG); mG.connect(car.frequency); car.connect(lp); lp.connect(amp);
    src.push(car, mod);
    play = (t, f, vel, dur) => {
      for (const prm of [car.frequency, mod.frequency, mG.gain, amp.gain]) hold(prm, t);
      car.frequency.setValueAtTime(f, t); mod.frequency.setValueAtTime(f, t);
      mG.gain.setTargetAtTime(p.index * f, t, 0.002); mG.gain.setTargetAtTime(0.12 * f, t + 0.006, 0.09);
      const A = 0.42 * vel * L;
      amp.gain.setTargetAtTime(A, t, 0.002); amp.gain.setTargetAtTime(0, t + 0.006, 0.6);
      amp.gain.setTargetAtTime(0, t + Math.max(dur, 0.6), 0.3);
    };
  }
  const P = persist(registry, tag, src, nodes, t0, offset);
  let last = -Infinity;
  return {
    note(t, midi, vel, dur, glide = false) {
      if (t - last < gap) return;
      last = t;
      play(t, mtof(midi), vel, Math.max(0.06, dur), glide);
    },
    dispose(t) { P.dispose(t); },
  };
}
